/**
 * Forward view — `GET /fundings/forward?days=N[&los=]` (closing-readiness.md
 * §5): the anticipated fundings for the next N days across BOTH pipelines and
 * both LOS, with pull-through probability, probability-weighted amount, and
 * lock-expiration highlights.
 *
 * What the desk needs to spot at a glance (per the doc): `lockExpiring`
 * (inside the window or before the loan's funding date), loans that keep
 * showing up (`timesInScope` ≥ 2) and loans that keep moving
 * (`expectedDateChangeCount`). A day strip up top, one AG Grid below.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import PlaylistAddRounded from "@mui/icons-material/PlaylistAddRounded";
import SyncRounded from "@mui/icons-material/SyncRounded";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import { Pill, ReadinessBadge } from "../../components/closingReadiness/ReadinessBits";
import {
  eventNoun,
  formatCompactDollar,
  pipelineStatusLabel,
} from "../../components/closingReadiness/closingReadinessUtils";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { fundingsApi } from "../../api/fundingsApi";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { ActionCell, actionColumn } from "../admin/shared/adminTableActions";
import { amber, slate, status, steel } from "../../theme/colors";
import { C, CenterCell, MONO, StatCard, loanHref, pct } from "./fundingsUi";

const DAY_OPTIONS = [5, 10, 15, 30];
const LOS_OPTIONS = [
  { value: "all", label: "Both LOS" },
  { value: "this", label: "This LOS only" },
];

// One day in the strip: date, count, amount vs probability-weighted amount,
// ready + lock-expiring counts. Click filters the grid to that day.
const DayCard = ({ day, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      minWidth: 150,
      flex: "1 1 150px",
      px: 1.5,
      py: 1.25,
      borderRadius: "10px",
      cursor: "pointer",
      background: active ? C.ink : C.surface,
      color: active ? "#fff" : C.ink,
      border: `1px solid ${active ? C.ink : C.line}`,
      boxShadow: "0 1px 2px rgba(20,30,50,.06)",
      transition: "background 0.12s",
      "&:hover": { borderColor: active ? C.ink : C.faint },
    }}
  >
    <Flx ac jb g={1}>
      <Txt sx={{ fontSize: 12.5, fontWeight: 800, color: active ? "#fff" : C.ink }}>
        {formatDate(day.date, "—", "EEE MMM d")}
      </Txt>
      <Txt sx={{ fontSize: 11.5, fontWeight: 700, color: active ? "rgba(255,255,255,.75)" : C.faint }}>
        {day.count}
      </Txt>
    </Flx>
    <Txt sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: active ? "#fff" : C.ink, display: "block", mt: 0.5 }}>
      {formatCompactDollar(day.amount) || "$0"}
    </Txt>
    <Txt sx={{ fontSize: 11, color: active ? "rgba(255,255,255,.75)" : C.sub, display: "block" }}>
      {formatCompactDollar(day.expectedAmount) || "$0"} weighted · {day.readyCount} ready
      {day.lockExpiring ? ` · ${day.lockExpiring} lock exp.` : ""}
    </Txt>
  </Box>
);

const ForwardFundingsView = ({ tapes, onTapesChanged }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [days, setDays] = useState(5);
  const [los, setLos] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dayFilter, setDayFilter] = useState(null);
  const [menu, setMenu] = useState(null); // { anchor, row }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fundingsApi.forward({ days, los: los === "all" ? undefined : los }));
    } catch (err) {
      console.error("forward fundings error:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, los]);

  useEffect(() => {
    load();
  }, [load]);

  // Rebuild the auto-scope records for the window, then reload.
  const sync = async () => {
    setSyncing(true);
    try {
      const start = new Date();
      const end = new Date(start.getTime() + days * 86400000);
      const ymd = (d) => d.toISOString().slice(0, 10);
      await fundingsApi.sync({ start: ymd(start), end: ymd(end) });
      enqueueSnackbar("Funding records rebuilt for the window.", { variant: "success" });
      load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Sync failed.", { variant: "error" });
    } finally {
      setSyncing(false);
    }
  };

  const byDay = useMemo(() => (Array.isArray(data?.byDay) ? data.byDay : []), [data]);
  const rows = useMemo(
    () =>
      byDay
        .filter((d) => !dayFilter || d.date === dayFilter)
        .flatMap((d) => (Array.isArray(d.items) ? d.items : []).map((it) => ({ ...it, _day: d.date }))),
    [byDay, dayFilter],
  );
  const totals = data?.totals || {};
  const openTapes = (tapes || []).filter((t) => t.status === "open");

  const addToTape = async (tapeId) => {
    const row = menu?.row;
    setMenu(null);
    if (!row?.dealId || !tapeId) return;
    try {
      await fundingsApi.addLoans(tapeId, [row.dealId]);
      enqueueSnackbar(`Added #${row.loanNumber ?? row.dealId} to ${tapeId}.`, { variant: "success" });
      load();
      if (typeof onTapesChanged === "function") onTapesChanged();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Could not add the loan.", { variant: "error" });
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Loan",
        colId: "loan",
        minWidth: 220,
        flex: 1.4,
        valueGetter: (p) => `${p.data?.loanNumber ?? ""} ${p.data?.loanName ?? ""}`,
        cellRenderer: (p) => (
          <CenterCell left>
            <Box sx={{ minWidth: 0 }}>
              <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: C.ink, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.data?.loanNumber != null ? `#${p.data.loanNumber} · ` : ""}
                {p.data?.loanName || "—"}
              </Txt>
              <Txt sx={{ fontFamily: MONO, fontSize: 10.5, color: C.faint, display: "block" }}>
                {[p.data?.syncLoanId, p.data?.originatingLos, eventNoun(p.data).toLowerCase()].filter(Boolean).join(" · ")}
              </Txt>
            </Box>
          </CenterCell>
        ),
      },
      {
        headerName: "Status",
        colId: "status",
        minWidth: 150,
        flex: 1,
        valueGetter: (p) => pipelineStatusLabel(p.data),
      },
      {
        headerName: "Readiness",
        colId: "readiness",
        width: 150,
        valueGetter: (p) => p.data?.readinessLevel,
        cellRenderer: (p) => (
          <CenterCell>
            <ReadinessBadge level={p.data?.readinessLevel} signal={p.data?.fundingSignal} size="xs" />
          </CenterCell>
        ),
      },
      {
        headerName: "Amount",
        colId: "amount",
        width: 120,
        type: "rightAligned",
        valueGetter: (p) => p.data?.amount,
        valueFormatter: (p) => formatDollar(p.value, "—"),
        cellStyle: { fontFamily: MONO },
      },
      {
        headerName: "Pull-through",
        colId: "pull",
        width: 110,
        valueGetter: (p) => p.data?.pullThroughProbability,
        cellRenderer: (p) => {
          const v = Number(p.value ?? 0);
          const tone = v >= 0.75 ? status.success : v >= 0.4 ? { fg: amber.chipText, bg: amber.light } : status.error;
          return (
            <CenterCell>
              <Pill label={pct(p.value)} c={tone.fg} bg={tone.bg} size="xs" />
            </CenterCell>
          );
        },
      },
      {
        headerName: "Funding date",
        colId: "date",
        width: 130,
        valueGetter: (p) => p.data?.expectedFundingDate,
        valueFormatter: (p) => `${formatDate(p.value, "—")}${p.data?.fundingDateBasis === "expected" ? " (est.)" : ""}`,
      },
      {
        headerName: "Lock",
        colId: "lock",
        width: 140,
        valueGetter: (p) => p.data?.lockExpirationDate,
        cellRenderer: (p) => {
          const d = p.data;
          if (!d?.lockExpirationDate) return <CenterCell>—</CenterCell>;
          const bad = d.lockExpiring || d.lockExpiresBeforeFunding;
          return (
            <CenterCell>
              <Pill
                label={`${formatDate(d.lockExpirationDate, "—", "MMM d")}${d.lockExpiresBeforeFunding ? " · before funding" : d.lockExpiring ? " · expiring" : ""}`}
                c={bad ? status.error.fg : slate[600]}
                bg={bad ? status.error.bg : slate[100]}
                size="xs"
              />
            </CenterCell>
          );
        },
      },
      {
        headerName: "Seen",
        colId: "scope",
        width: 90,
        headerTooltip: "How many forward windows this loan has appeared in",
        valueGetter: (p) => p.data?.timesInScope ?? 0,
        cellRenderer: (p) => (
          <CenterCell>
            <Pill
              label={`${p.value}×`}
              c={p.value >= 2 ? amber.chipText : slate[600]}
              bg={p.value >= 2 ? amber.light : slate[100]}
              size="xs"
            />
          </CenterCell>
        ),
      },
      {
        headerName: "Moved",
        colId: "moved",
        width: 90,
        headerTooltip: "Expected-date changes",
        valueGetter: (p) => p.data?.expectedDateChangeCount ?? 0,
        cellRenderer: (p) => (
          <CenterCell>
            <Pill
              label={`${p.value}×`}
              c={p.value >= 2 ? amber.chipText : slate[600]}
              bg={p.value >= 2 ? amber.light : slate[100]}
              size="xs"
            />
          </CenterCell>
        ),
      },
      {
        headerName: "Closer",
        colId: "closer",
        minWidth: 160,
        flex: 1,
        valueGetter: (p) => p.data?.closer,
        cellRenderer: (p) => (
          <CenterCell left>
            <Box sx={{ minWidth: 0 }}>
              <Txt sx={{ fontSize: 12, color: C.ink, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.value || "—"}
              </Txt>
              {p.data?.closerLastActiveAt && (
                <Txt sx={{ fontSize: 10.5, color: p.data?.closerActive ? status.success.fg : C.faint, display: "block" }}>
                  {p.data.closerActive ? "active · " : ""}
                  {formatDateTime(p.data.closerLastActiveAt, "date")}
                </Txt>
              )}
            </Box>
          </CenterCell>
        ),
      },
      {
        headerName: "Tapes",
        colId: "tapes",
        width: 150,
        valueGetter: (p) => (p.data?.activeTapeIds || []).join(", "),
        cellRenderer: (p) => (
          <CenterCell>
            <Flx ac wrap g={0.4}>
              {(p.data?.activeTapeIds || []).length === 0 ? (
                <Txt sx={{ fontSize: 11.5, color: C.faint }}>—</Txt>
              ) : (
                p.data.activeTapeIds.map((t) => <Pill key={t} label={t} c={steel.main} bg={steel.tint} size="xs" />)
              )}
            </Flx>
          </CenterCell>
        ),
      },
      actionColumn({
        colId: "actions",
        count: 2,
        cellRenderer: (p) => {
          const href = loanHref(p.data);
          return (
            <ActionCell>
              <Tooltip title={href ? "Open loan" : "Lives in the other LOS"}>
                <span>
                  <IconButton size="small" disabled={!href} onClick={() => href && navigate(href)}>
                    <OpenInNewRounded fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Add to tape">
                <IconButton size="small" onClick={(e) => setMenu({ anchor: e.currentTarget, row: p.data })}>
                  <PlaylistAddRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </ActionCell>
          );
        },
      }),
    ],
    [navigate],
  );

  return (
    <Box>
      {/* Controls */}
      <Flx ac jb wrap g={1.5} sx={{ mb: 2 }}>
        <Flx ac wrap g={1.5}>
          <TextField
            select
            size="small"
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setDayFilter(null);
            }}
            sx={{ minWidth: 130, background: C.surface, ".MuiInputBase-root": { fontSize: 13 } }}
          >
            {DAY_OPTIONS.map((d) => (
              <MenuItem key={d} value={d} sx={{ fontSize: 13 }}>
                Next {d} days
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={los}
            onChange={(e) => setLos(e.target.value)}
            sx={{ minWidth: 140, background: C.surface, ".MuiInputBase-root": { fontSize: 13 } }}
          >
            {LOS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          {loading && <CircularProgress size={16} sx={{ color: C.faint }} />}
        </Flx>
        <Button
          size="small"
          variant="outlined"
          color="gray"
          startIcon={syncing ? <CircularProgress size={12} /> : <SyncRounded />}
          onClick={sync}
          disabled={syncing}
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          Rebuild window
        </Button>
      </Flx>

      {/* Totals */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
        <StatCard label="Loans in window" value={totals.count ?? 0} sub={`${totals.readyCount ?? 0} ready to fund`} />
        <StatCard label="Projected amount" value={formatCompactDollar(totals.amount) || "$0"} sub={formatDollar(totals.amount, "$0")} />
        <StatCard label="Probability-weighted" value={formatCompactDollar(totals.expectedAmount) || "$0"} sub="Σ amount × pull-through" />
        <StatCard label="Locks expiring" value={totals.lockExpiring ?? 0} alert={(totals.lockExpiring ?? 0) > 0} sub="inside the window or before funding" />
      </Box>

      {/* Day strip */}
      {byDay.length > 0 && (
        <Flx wrap g={1} sx={{ mb: 2 }}>
          {byDay.map((d) => (
            <DayCard
              key={d.date}
              day={d}
              active={dayFilter === d.date}
              onClick={() => setDayFilter((cur) => (cur === d.date ? null : d.date))}
            />
          ))}
        </Flx>
      )}

      <AgGridTableWrapper height="auto">
        <AgGridReact
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={{
            resizable: true,
            sortable: true,
            suppressHeaderMenuButton: true,
            minWidth: 80,
            cellStyle: { display: "flex", alignItems: "center" },
          }}
          getRowId={(p) => p.data?.id || `${p.data?.originatingLos}:${p.data?.dealId}`}
          domLayout="autoHeight"
          pagination
          paginationPageSize={25}
          rowHeight={50}
          headerHeight={38}
          suppressCellFocus
          overlayNoRowsTemplate={
            loading ? "<span></span>" : "<span style='font-size:12.5px;color:#94a3b8'>No fundings in this window</span>"
          }
        />
      </AgGridTableWrapper>

      <Menu open={Boolean(menu)} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        {openTapes.length === 0 ? (
          <MenuItem disabled sx={{ fontSize: 13 }}>
            No open tapes
          </MenuItem>
        ) : (
          openTapes.map((t) => (
            <MenuItem
              key={t.id}
              onClick={() => addToTape(t.id)}
              disabled={(menu?.row?.activeTapeIds || []).includes(t.id)}
              sx={{ fontSize: 13 }}
            >
              {t.name || t.id}
              {(menu?.row?.activeTapeIds || []).includes(t.id) ? " · already on" : ""}
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};

export default ForwardFundingsView;
